import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { useAuth } from "@/hooks/auth";
import { getUserPatterns } from "@/store";
import { Patterns, Pattern } from "@/store/types";
import { layout } from "@/constants";
import {
  ScreenLayout,
  ScrollView,
  Spinner,
  Title,
  Text,
  PatternCard,
  DisclaimerFooter
} from "@/components";
import { logger } from "@/utils/logger";

/**
 * Sorts patterns by significance (high → medium → low), then alphabetically
 * @param patterns - Array of patterns to sort
 * @returns Sorted array of patterns
 */
const sortPatterns = (patterns: Pattern[]): Pattern[] => {
  const significanceOrder = { high: 3, medium: 2, low: 1 };

  return [...patterns].sort((a, b) => {
    // First, sort by significance (descending)
    const sigDiff =
      significanceOrder[b.significance] - significanceOrder[a.significance];
    if (sigDiff !== 0) return sigDiff;

    // Then, sort alphabetically by name
    return a.name.localeCompare(b.name);
  });
};

/**
 * Patterns screen component
 * Displays user patterns as cards sorted by significance
 */
export default function PatternsScreen() {
  const { user } = useAuth();
  const [patternsData, setPatternsData] = useState<Patterns | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) return;

    const loadPatterns = async () => {
      try {
        setLoading(true);
        const data = await getUserPatterns(user.uid);
        setPatternsData(data);
        setError(null);
      } catch (err) {
        logger.error("Failed to load patterns:", err);
        setError("Failed to load patterns. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadPatterns();
  }, [user?.uid]);

  const sortedPatterns = sortPatterns(patternsData?.patterns ?? []);

  return (
    <ScreenLayout>
      <ScrollView>
        <Title subtitle="These are your repeated patterns of thinking, feeling, or behaving">
          Patterns
        </Title>

        {loading ? (
          <Spinner style={styles.loadingContainer} />
        ) : error ? (
          <View style={styles.errorContainer}>
            <Text size="medium" color="primary">
              {error}
            </Text>
          </View>
        ) : sortedPatterns.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text size="small" color="tertiary" style={styles.emptyText}>
              No patterns yet. After a few sessions, recurring themes will
              appear here.
            </Text>
          </View>
        ) : (
          <View style={styles.listContent}>
            {sortedPatterns.map((item, index) => (
              <React.Fragment key={item.name}>
                {index > 0 && <View style={styles.separator} />}
                <PatternCard pattern={item} />
              </React.Fragment>
            ))}
          </View>
        )}
      </ScrollView>
      <DisclaimerFooter />
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    paddingVertical: layout.space.xl
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: layout.space.xl
  },
  emptyContainer: {
    alignItems: "center",
    paddingVertical: layout.space.xl
  },
  emptyText: {
    textAlign: "center",
    fontStyle: "italic"
  },
  listContent: {
    paddingBottom: layout.space.xl
  },
  separator: {
    height: layout.space.md
  }
});
